<script>
import appConfig from '@src/app.config'
import { authComputed, globalComputed } from '@store/helpers'
import { getLocalStorage } from '@utils/localStorage'
import _throttle from 'lodash/throttle'
import getScrollTop from '@utils/get-scroll-top'
import store from '@store'
import CookieModal from '@components/Base/CookieModal.vue'

export default {
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      const setting = store.state.global.currentSetting || ''
      title = typeof title === 'function' ? title(this.$store) : title
      return title ? `${title} | ${setting.sitename || appConfig.title}` : setting.sitename || appConfig.title
    },
  },
  name: 'App',
  components: {
    CookieModal,
  },
  data() {
    return {
      varVh: 0,
      showCookieModal: !getLocalStorage('zerone-cookie'),
    }
  },
  computed: {
    ...authComputed,
    ...globalComputed,
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    isDisplayPreMode() {
      return getLocalStorage('set.displayPre') === true
    },
    isDisplayFbChat() {
      return this.$route.meta.isDisplayFbChat !== false
    },
    appVr() {
      return {
        '--vh': `${this.varVh}px`,
      }
    },
  },
  watch: {
    'window.metaData': {
      handler(newMeta) {
        this.$store.dispatch('meta/updateMetaData', newMeta || {})
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    const initScrollListener = () => {
      window.addEventListener('scroll', this.onScroll)
    }

    const initVarVh = () => {
      this.varVh = window.innerHeight * 0.01
    }

    initVarVh()

    window.addEventListener('resize', () => {
      initVarVh()
    })

    const initLocalStorageListener = () => {
      window.onstorage = (e) => {
        const authKey = 'auth.currentUser'
        if (e.key === authKey) {
          const newValue = JSON.parse(e.newValue)

          this.$store.dispatch('auth/setCurrentUser', newValue)

          const checkIsLoginPage = () => {
            if (this.$route.meta.isLoginPage) {
              if (!this.loggedIn) {
                this.$router.push({
                  name: 'login',
                  query: {
                    redirectFrom: location.pathname,
                  },
                })
              }
            }
          }

          const checkIsLogoutPage = () => {
            if (this.$route.meta.isLogoutPage) {
              if (this.loggedIn) {
                window.location = this.$route.query.redirectFrom || '/member'
              }
            }
          }

          checkIsLogoutPage()
          checkIsLoginPage()
        }
      }
    }

    initScrollListener()
    initLocalStorageListener()
  },
  methods: {
    onScroll: _throttle(function() {
      const scrollTop = getScrollTop()

      if (scrollTop > this.$globalScrollTop) {
        this.$globalDirection = 'down'
      } else {
        this.$globalDirection = 'up'
      }

      this.$globalScrollTop = scrollTop
    }, 200),
    closeCookieModal(value) {
      if (value) {
        this.showCookieModal = false
      }
    },
  },
}
</script>
<template>
  <div id="app" :class="{ isDevelopment: isDevelopment, isDisplayPreMode: isDisplayPreMode, isDisplayFbChat: isDisplayFbChat }" :style="appVr">
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    <RouterView :key="$route.fullPath" />
    <CookieModal v-if="showCookieModal" @close="closeCookieModal" />
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
$grid-gutter-width: $container-gap * 2;

@import '~bootstrap/scss/bootstrap';

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';

// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';
@import '@styles/index.scss';
</style>
